import { Link, useParams, useSearchParams } from 'react-router-dom';
import RoomCarousel from '../components/RoomCarousel';
import BookingRequestForm from '../components/BookingRequestForm';
import SafeIcon from '../common/SafeIcon';
import { amenities as amenityOptions } from '../data/site';
import { useSiteContent } from '../context/SiteContent';
import './RoomDetail.css';

function asLines(value) {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || '').split(/\r?\n|·/).map((item) => item.trim()).filter(Boolean);
}

export default function RoomDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { site } = useSiteContent();
  const room = site.rooms.find((item) => item.number === Number(id));
  const initialValues = {
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    guests: searchParams.get('guests') || '1'
  };

  if (!room) {
    return (
      <main className="not-found page-width">
        <h1>Room not found</h1>
        <Link className="text-link" to="/rooms">Return to Our Rooms</Link>
      </main>
    );
  }

  const roomAmenities = asLines(room.amenities ?? room.features);
  const keyFeatures = asLines(room.keyFeatures);

  return (
    <main>
      <section className="room-detail-hero page-width">
        <RoomCarousel room={room} />
        <div className="room-detail-copy">
          <span className="eyebrow">Room 0{room.number}</span>
          <h1>{room.title}</h1>
          <p>{room.description}</p>
          <div className="detail-price">{room.price}</div>
          <a className="button button-gold" href="#booking-request">
            Enquire about this room <SafeIcon name="ArrowRight" />
          </a>
        </div>
      </section>

      <section className="room-facts section-cream">
        <div className="page-width room-facts-inner">
          <div><span className="eyebrow">Room details</span><h2>Make yourself at home.</h2></div>
          <dl>
            <div><dt>Capacity</dt><dd>{room.capacity}</dd></div>
            {keyFeatures.length > 0 && (
              <div className="room-facts-list-row">
                <dt>Key Features:</dt>
                <dd>
                  <ul className="room-key-feature-list">
                    {keyFeatures.map((feature, index) => <li key={`${feature}-${index}`}>{feature}</li>)}
                  </ul>
                </dd>
              </div>
            )}
            {roomAmenities.length > 0 && (
              <div className="room-facts-list-row">
                <dt>Amenities</dt>
                <dd>
                  <ul className="room-amenity-list">
                    {roomAmenities.map((label, index) => {
                      const option = amenityOptions.find((item) => item.label === label);
                      return (
                        <li key={`${label}-${index}`}>
                          {option && <span className="room-amenity-icon"><SafeIcon name={option.icon.replace(/^Fi/, '')} /></span>}
                          <span>{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </dd>
              </div>
            )}
            <div><dt>Price</dt><dd>{room.price}</dd></div>
          </dl>
        </div>
      </section>

      <section className="section-pad page-width room-request" id="booking-request">
        <span className="eyebrow">Planning a stay?</span>
        <h2>Ask us about your dates.</h2>
        <p>Requests are not confirmed until the guest house confirms availability.</p>
        <BookingRequestForm initialValues={initialValues} roomName={room.title} />
      </section>
    </main>
  );
}